import React, { useState } from "react";
import styled from "styled-components";
import { Button, Spinner } from "@cinesys/ui/components";
import { useDeleteProfile } from "./service";

/**
 * Destructive confirmation for deleting a persisted profile. Names the profile explicitly.
 * Deleting removes the saved configuration; the profile's historical run records live in a
 * separate store and are retained.
 */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 55;
`;
const Card = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 480px;
  padding: 24px 26px;
`;
const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 18px;
  color: #fff;
`;
const Body = styled.p`
  margin: 0 0 10px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--miInkSoft);
  b {
    color: #fff;
  }
`;
const Fine = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--miInkFaint);
`;
const Err = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--miDanger);
`;
const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
`;

export default function DeleteProfileModal({
  profileId,
  profileName,
  onClose,
  onDeleted,
}: {
  profileId: string;
  profileName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const del = useDeleteProfile();
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setError(null);
    try {
      await del.mutateAsync(profileId);
      onDeleted();
    } catch {
      setError("Could not delete this workflow. Please try again.");
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Title>Delete workflow</Title>
        <Body>
          This permanently removes the saved configuration for{" "}
          <b>{profileName}</b> — its source, field mappings, target and
          schedule. This cannot be undone.
        </Body>
        <Fine>
          Any recorded run history for this workflow is retained, but will no
          longer be browsable here once the profile is gone.
        </Fine>
        {error && <Err>{error}</Err>}
        <Foot>
          <Button label="Cancel" type="default" onClick={onClose} />
          <Button
            label={del.isLoading ? "Deleting…" : "Delete workflow"}
            type="danger"
            disabled={del.isLoading}
            onClick={confirm}
          />
          {del.isLoading && <Spinner />}
        </Foot>
      </Card>
    </Overlay>
  );
}
